import { useCallback, useContext, useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import { Timestamp } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { useHistory } from 'react-router-dom';

import { FormDoc, FormQuestion, User } from '@vyce/core/src/types';
import {
  createFormRequest,
  createFormTemplateRequest,
  deleteFormRequest,
  updateFormRequest,
  updateFormTemplateRequest,
  duplicateFormRequest,
  addFormResourceIdsRequest,
} from '@vyce/core/src/api/forms';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import { uploadFileRequest } from '@vyce/core/src/api/storage';

export const useCreateFormData = ({
  companyId,
  form,
  user,
  isTemplate = false,
  initialSelectRecipientsOpen = false,
}: {
  companyId: string;
  form?: FormDoc | null;
  user: User;
  isTemplate?: boolean;
  initialSelectRecipientsOpen?: boolean;
}) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectRecipientsOpen, setSelectRecipientsOpen] = useState(false);

  useEffect(() => {
    if (form && initialSelectRecipientsOpen && !isTemplate) {
      setSelectRecipientsOpen(true);
    }
  }, [form, initialSelectRecipientsOpen, isTemplate]);
  const [loading, setLoading] = useState(false);
  const { showNotification, handleServerError } = useContext(NotificationContext);
  const history = useHistory();

  const methods = useForm<FormDoc>({
    defaultValues: {
      title: '',
      description: '',
      createdBy: '',
      editors: [],
      createdAt: new Date(),
      updatedAt: new Date(),
      questionCount: 0,
      version: 1,
      duplicatedFrom: null,
      targets: {},
      questions: [],
      status: 'draft',
    },
  });

  const questions = methods.watch('questions');
  const title = methods.watch('title');

  const disableSelectRecipients = questions.length === 0 || !title;

  const findQuestion = (id: string) => {
    const currentQuestions = methods.getValues('questions');
    const numericIndex = parseInt(id);
    if (!isNaN(numericIndex) && numericIndex >= 0 && numericIndex < currentQuestions.length) {
      return {
        question: currentQuestions[numericIndex],
        index: numericIndex,
      };
    }

    const index = currentQuestions.findIndex(question => question.id === id);
    return {
      question: currentQuestions[index],
      index,
    };
  };

  const moveQuestion = (dragId: string, hoverIndex: number) => {
    const currentQuestions = methods.getValues('questions');
    const { index: dragIndex } = findQuestion(dragId);

    if (dragIndex === -1 || dragIndex >= currentQuestions.length) return;

    const draggedQuestion = currentQuestions[dragIndex];
    const newQuestions = [...currentQuestions];

    newQuestions.splice(dragIndex, 1);
    newQuestions.splice(hoverIndex, 0, draggedQuestion);

    const updatedQuestions = newQuestions.map((question, index) => ({
      ...question,
      order: index,
    }));

    methods.setValue('questions', updatedQuestions);
  };

  const handleRemoveQuestion = (questionId: string) => {
    const currentQuestions = methods.getValues('questions');
    const updatedQuestions = currentQuestions.filter(question => question.id !== questionId);
    methods.setValue('questions', updatedQuestions);
  };

  const duplicateQuestion = (questionId: string) => {
    const currentQuestions = methods.getValues('questions');
    const { question, index } = findQuestion(questionId);

    if (!question) return;

    const newQuestion: FormQuestion = {
      ...question,
      id: uuidv4(),
      order: currentQuestions.length,
      title: `${question.title} (copy)`,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    const updatedQuestions = [...currentQuestions, newQuestion];
    methods.setValue('questions', updatedQuestions);
    scrollToBottom();
  };

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      const formElement = document.querySelector('html');
      if (formElement) {
        formElement.scrollTo({
          top: formElement.scrollHeight,
          behavior: 'smooth',
        });
      }
    }, 100);
  }, []);

  const addQuestion = () => {
    const newQuestion: FormQuestion = {
      id: uuidv4(),
      order: methods.getValues('questions').length,
      title: '',
      answer: { type: 'short_text', options: [], min: 0, max: 10, minLabel: '', maxLabel: '' },
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    methods.setValue('questions', [...methods.getValues('questions'), newQuestion]);
    scrollToBottom();
  };

  const onSelectRecipientsClick = () => {
    setSelectRecipientsOpen(true);
  };

  const uploadFileToFirebaseStorage = async (file: File): Promise<string | null> => {
    try {
      const result = await uploadFileRequest(file);
      return result?.data.url || null;
    } catch (e) {
      console.error(e);
      return null;
    }
  };

  const createForm = async ({
    status,
    endDate,
    signature,
    clockInBlock,
    folderId,
  }: {
    status?: string;
    endDate?: Date;
    signature?: boolean;
    clockInBlock?: boolean;
    folderId?: string | null;
  } = {}): Promise<string | null> => {
    const valid = await methods.trigger();
    if (!valid) {
      showNotification({ message: 'Please fill in all questions', options: { variant: 'error' } });
      return null;
    }
    setLoading(true);
    try {
      const formData = methods.getValues();

      if (isTemplate) {
        await createFormTemplateRequest({
          companyId,
          userId: user.uuid,
          userName: [user?.first_name, user?.last_name].filter(Boolean).join(' ') || undefined,
          title: formData.title,
          description: formData.description || '',
          questions: formData.questions || [],
          signature: formData.signature ?? false,
          clockInBlock: formData.clockInBlock ?? false,
        });
        showNotification({ message: 'Form template has been created', options: { variant: 'success' } });
        methods.reset();
        history.push(`/forms/form-templates`);
        return null;
      }

      const res = await createFormRequest({
        companyId: companyId,
        userId: user.uuid,
        userName: user?.first_name + ' ' + user?.last_name,
        title: formData.title,
        description: formData.description,
        questions: formData.questions,
        endDate,
        status: status ?? 'draft',
        signature,
        clockInBlock,
        folderId,
      });
      showNotification({ message: 'Form has been created', options: { variant: 'success' } });
      methods.reset();
      history.push(`/forms/my-forms`);
      return res?.formId;
    } catch (error) {
      handleServerError(error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateForm = async ({
    formId,
    status,
    endDate,
    signature,
    clockInBlock,
    folderId,
    stayOnPage = false,
    silentSuccess = false,
  }: {
    formId: string;
    status: string;
    endDate?: Date;
    signature?: boolean;
    clockInBlock?: boolean;
    folderId?: string | null;
    stayOnPage?: boolean;
    silentSuccess?: boolean;
  }) => {
    const valid = await methods.trigger();
    if (!valid) {
      showNotification({ message: 'Please fill in all questions', options: { variant: 'error' } });
      return null;
    }
    setLoading(true);
    try {
      const formData = methods.getValues();

      if (isTemplate) {
        await updateFormTemplateRequest({
          companyId,
          templateId: formId,
          userId: user.uuid,
          title: formData.title,
          description: formData.description,
          questions: formData.questions,
          signature: formData.signature ?? false,
          clockInBlock: formData.clockInBlock ?? false,
        });
        showNotification({ message: 'Form template has been updated', options: { variant: 'success' } });
        methods.reset();
        history.push(`/forms/form-templates`);
        return;
      }

      await updateFormRequest({
        companyId: companyId,
        formId,
        userId: user.uuid,
        title: formData.title,
        description: formData.description,
        questions: formData.questions,
        endDate,
        signature,
        status,
        clockInBlock,
        folderId,
      });
      if (!silentSuccess) {
        showNotification({ message: 'Form has been updated', options: { variant: 'success' } });
      }
      if (stayOnPage) {
        return;
      }
      methods.reset();
      history.push(`/forms/my-forms`);
    } catch (error) {
      handleServerError(error);
    } finally {
      setLoading(false);
    }
  };

  const duplicateForm = async (formId: string): Promise<string | null> => {
    setLoading(true);
    try {
      const result = await duplicateFormRequest({ companyId: companyId, formId, userId: user.uuid });
      return result?.formId || null;
    } catch (error) {
      handleServerError(error);
      return null;
    } finally {
      setLoading(false);
    }
  };
  const deleteForm = async (formId: string) => {
    setLoading(true);
    try {
      await deleteFormRequest({ companyId: companyId, formId, userId: user.uuid });
      showNotification({ message: 'Form has been deleted', options: { variant: 'success' } });
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const onSendToResourceWorkers = async (formId: string, resourceIds: string[]) => {
    try {
      setLoading(true);
      await addFormResourceIdsRequest({
        companyId,
        formId,
        userId: user.uuid,
        resourceIds,
      });
    } catch (error) {
      handleServerError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (history.location.search.includes('?preview=true')) {
      setPreviewOpen(true);
    } else {
      setPreviewOpen(false);
    }
  }, [history.location.pathname]);

  useEffect(() => {
    if (form) {
      methods.reset(form);
    }
  }, [form]);

  return {
    methods,
    addQuestion,
    onSelectRecipientsClick,
    handleRemoveQuestion,
    questions,
    findQuestion,
    moveQuestion,
    duplicateQuestion,
    previewOpen,
    setPreviewOpen,
    createForm,
    updateForm,
    loading,
    deleteForm,
    selectRecipientsOpen,
    setSelectRecipientsOpen,
    disableSelectRecipients,
    duplicateForm,
    uploadFileToFirebaseStorage,
    onSendToResourceWorkers,
  };
};
